// AI-CONTEXT: See docs/features/arbeidsvaardigheden.md
import berekenEffectscores from './berekenEffectscores'
import { STANDAARD_VERSIE } from './arbeidsvaardighedenStandaard'

/**
 * Het document dat één beoordeling arbeidsvaardigheden vastlegt.
 *
 * Elke beoordeling is een eigen document, geen t0/t1-paar op de persona zoals bij
 * de participatieladder. De begeleider beoordeelt opnieuw wanneer dat aan de orde
 * is - richtlijn een kwartaal - en dan hoort er een meting bij te komen, niet een
 * bestaande overschreven te worden. De eerste is de 0-meting, de laatste de
 * 1-meting, en alles ertussen blijft zichtbaar.
 *
 * De scores worden meegeschreven en niet alleen de antwoorden. Een benchmark of
 * export moet ze kunnen lezen zonder de standaard opnieuw toe te passen, en een
 * latere versie van de standaard mag een oude meting niet met terugwerkende
 * kracht een andere score geven. `StandaardVersie` zegt welke lijst gold.
 *
 * Wie beoordeelde staat erbij. Dit is een oordeel van een begeleider over een
 * ander mens; zonder naam erbij is het niet navraagbaar.
 */

/** Lokale dagsleutel (yyyy-mm-dd) - lokale tijdzone, zodat een avond-save niet op de UTC-volgende dag belandt. */
const dagSleutel = (date) => {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

/**
 * Scores per effect-id uit de antwoorden, plus of de hele beoordeling af is.
 *
 * @param {Object<string, Object<number, number>>} antwoorden
 * @returns {{scores: Object<string, number|null>, compleet: boolean}}
 */
const scoresVanAntwoorden = (antwoorden) => {
    const rijen = berekenEffectscores(antwoorden)
    return {
        scores: Object.fromEntries(rijen.map((r) => [r.id, r.score])),
        compleet: rijen.every((r) => r.compleet),
    }
}

/**
 * Payload van een nieuwe, lege beoordeling.
 *
 * @param {{id: string, client: string, personaId: string, authId: string, naam?: string, timestamp: *, datum?: Date}} input
 * @returns {object}
 */
const nieuweBeoordeling = ({ id, client, personaId, authId, naam, timestamp, datum = new Date() }) => ({
    ID: id,
    CompagnyID: client,
    Compagny: client,
    PersonaID: personaId,
    StandaardVersie: STANDAARD_VERSIE,
    Antwoorden: {},
    Scores: {},
    Compleet: false,
    BeoordeeldDoor: authId || '',
    BeoordeeldDoorNaam: naam || '',
    Datum: dagSleutel(datum),
    // Het aanmaakmoment, tot op de seconde. Datum heeft dagprecisie, dus twee
    // beoordelingen op dezelfde dag zijn daarop niet uit elkaar te houden; dit
    // veld beslist dan welke de nieuwste is. Het wordt na aanmaken nooit meer
    // geschreven - Timestamp doet dat wel (bij elk antwoord), en sorteren op
    // laatst-gewijzigd zou het bewerken van de oudste die tot 'de nieuwste'
    // promoveren. Bewust een gewone tijdstempel en geen serverTimestamp: die is
    // direct na aanmaken nog null, precies wanneer de beoordeling open moet.
    Created: datum.toISOString(),
    Timestamp: timestamp,
})

/**
 * De velden die één antwoord wegschrijft. De scores gaan mee, zodat het document
 * nooit een antwoordenset draagt met scores die daar niet bij horen.
 *
 * @param {{antwoorden: Object, effectId: string, nummer: number, waarde: number|null}} input
 * @returns {object} platte update-map voor Firestore
 */
const antwoordUpdate = ({ antwoorden, effectId, nummer, waarde }) => {
    const bijgewerkt = {
        ...antwoorden,
        [effectId]: { ...(antwoorden?.[effectId] || {}), [nummer]: waarde },
    }
    if (waarde === null) delete bijgewerkt[effectId][nummer]

    const { scores, compleet } = scoresVanAntwoorden(bijgewerkt)
    return { Antwoorden: bijgewerkt, Scores: scores, Compleet: compleet }
}

/**
 * De velden die een gewijzigde beoordelingsdatum wegschrijft.
 *
 * `Created` blijft staan. Dat is het aanmaakmoment van het formulier en het
 * breekt het gelijkspel tussen twee beoordelingen op dezelfde dag; wie de datum
 * verzet, verzet de meting, niet het moment waarop het formulier ontstond.
 *
 * Een lege of onleesbare datum geeft null in plaats van een lege string:
 * `Datum` stuurt de sortering, de groeiberekening (eerste tegen laatste) en de
 * statuspil, en een lege waarde zou de beoordeling stil vooraan zetten - dan
 * wordt hij de nulmeting waartegen alle andere gelezen worden.
 *
 * Een datum in de toekomst ook niet: een beoordeling legt vast wat er al
 * gebeurd is. `beoordelingStatus` vangt zo'n datum wel op (hij klemt op 0
 * maanden), maar dan staat er nog steeds een meting in de reeks die nooit
 * gedaan is.
 *
 * @param {*} datum - yyyy-mm-dd uit het datumveld
 * @param {Date} [nu] - Bovengrens; parameter zodat een test niet van de klok afhangt
 * @returns {{Datum: string}|null} null als de datum niet deugt
 */
const datumUpdate = (datum, nu = new Date()) => {
    if (typeof datum !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(datum)) return null

    const gekozen = new Date(`${datum}T00:00:00`)
    if (Number.isNaN(gekozen.getTime())) return null

    // 31 februari rolt in JavaScript stil door naar 3 maart. De heenweg terug
    // vergelijken is de enige manier om zo'n dag te herkennen.
    if (dagSleutel(gekozen) !== datum) return null

    if (datum > dagSleutel(nu)) return null

    return { Datum: datum }
}

// AI-CONTEXT: See docs/pitfalls.md#database-coarse-sort-key-ties-break-arbitrarily
/**
 * Vergelijking op meetmoment, oudste eerst. Eén regel voor de hele feature: het
 * tabblad leest hem omgekeerd (nieuwste eerst) en de groeiberekening in
 * `groeiPerEffect` gebruikt hem zoals hij is, om de eerste en de laatste meting
 * aan te wijzen. Twee plekken die los van elkaar sorteren zouden het bij een
 * gelijke datum oneens kunnen zijn over welke meting de nulmeting is.
 *
 * DIT IS DE BRON. `functions/workSkillsHelpers/buildWorkSkillsBenchmark.js` heeft
 * een spiegel voor de cross-tenant benchmark; die twee worden vergeleken door
 * `functions/workSkillsHelpers/oudsteEerstPariteit.test.js`. Wijzig je hier iets,
 * dan wordt die test rood tot de spiegel volgt — pas nooit de spiegel aan om deze
 * kant te "repareren".
 *
 * @param {object} a
 * @param {object} b
 * @returns {number} negatief als `a` ouder is
 */
const oudsteEerst = (a, b) =>
    String(a?.Datum || '').localeCompare(String(b?.Datum || ''))
    || String(a?.Created || '').localeCompare(String(b?.Created || ''))
    || String(a?.ID || '').localeCompare(String(b?.ID || ''))

/**
 * Nieuwste eerst.
 *
 * Er wordt op `Datum` gesorteerd en niet op `Timestamp`: een net aangemaakt
 * document heeft nog een pending serverTimestamp (null) en zou dan achteraan
 * belanden, precies op het moment dat de begeleider hem wil invullen.
 *
 * `Datum` heeft dagprecisie, dus twee beoordelingen op dezelfde dag eindigen
 * gelijk. Dan besliste de volgorde van Firestore welke 'de nieuwste' was, en
 * daarmee welke van de twee bewerkbaar was - wisselend per snapshot, waardoor
 * een gloednieuwe lege beoordeling op alleen-lezen kon staan. `Created` breekt
 * dat gelijkspel; beoordelingen van vóór dat veld hebben het niet en komen
 * binnen hun dag achteraan, met `ID` als laatste redmiddel zodat de volgorde in
 * elk geval niet meer verspringt.
 *
 * @param {Array<object>} beoordelingen
 * @returns {Array<object>} nieuwste eerst
 */
const gesorteerd = (beoordelingen = []) =>
    [...beoordelingen].sort((a, b) => oudsteEerst(b, a))

/**
 * Mogen de ANTWOORDEN van deze beoordeling nog gewijzigd worden?
 *
 * Alleen de antwoorden. De datum valt hier buiten en blijft altijd te
 * corrigeren: een jaartal dat bij het invullen misging was anders nergens meer
 * recht te zetten, ook niet met een nieuwe beoordeling (besluit gebruiker
 * 2026-09-22, docs/inbox.md #277). De pagina leest deze uitkomst daarom als
 * `antwoordenBewerkbaar`, niet als `bewerkbaar`.
 *
 * De nieuwste altijd - dat is degene waar iemand aan werkt.
 *
 * Van de eerdere alleen degene die nooit is afgerond. Een afgeronde beoordeling
 * is een vastgelegd oordeel op een moment, en op het eerste effect ook de
 * nulmeting waartegen alle latere metingen worden gelezen; wie die bijwerkt
 * verschuift dat beginpunt zonder dat het ergens zichtbaar wordt. Een
 * onafgeronde legde dat niet vast: effecten zonder volledige set antwoorden
 * hebben `Scores[effectId] === null` en tellen nergens als meting mee. Hem
 * vergrendelen laat de begeleider achter met werk dat hij niet meer af kan
 * maken (besluit gebruiker 2026-08-31, aanscherping van besluit 3).
 *
 * @param {object|null} beoordeling - De beoordeling die open staat
 * @param {object|null} laatste - De nieuwste beoordeling van deze deelnemer
 * @returns {boolean}
 */
const isBewerkbaar = (beoordeling, laatste) => {
    if (!beoordeling) return false
    if (laatste && beoordeling.ID === laatste.ID) return true
    return beoordeling.Compleet !== true
}

export {
    dagSleutel,
    scoresVanAntwoorden,
    nieuweBeoordeling,
    antwoordUpdate,
    datumUpdate,
    oudsteEerst,
    gesorteerd,
    isBewerkbaar,
}
